// ProductSearch component - Store finder product search tab
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import FlavorImage from './FlavorImage';
import ProductDropdown from './ProductDropdown';
import SortDropdown from './SortDropdown';
import ProductFilterDropdown from './ProductFilterDropdown';
import PropTypes from 'prop-types';
import { trackProductView, trackProductDetails, trackProductSearch } from '../utils/analytics';
import { getCanonicalProductInfo } from '../utils/productMatcher';
import { initialize as initializeSyncMatcher, getCanonicalProductSync } from '../utils/syncProductMatcher';
import getProductImagePath from '../utils/productImages';
import ProductGrid from './ProductGrid';
import MobileProductVideo from './MobileProductVideo';
import { authenticatedFetch } from '../utils/authService';
import ProductsUnavailable from './ProductsUnavailable';
import './MobileProductVideo.scss';
import './ProductCard.scss';
import './StoreNameLink.scss'; // Import styles for enhanced store name links


// Preload canonical products and initialize sync matcher at component initialization
initializeSyncMatcher().catch(error => {
  console.error('Failed to initialize sync product matcher in ProductSearch:', error);
});

// Export functions to make them available to other modules (for backward compatibility)
export { getCanonicalProductInfo } from '../utils/productMatcher';
export { getCanonicalProductSync } from '../utils/syncProductMatcher';

const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.MODE === 'production' ? 'https://good-feels-store-selector-api.fly.dev' : 'http://localhost:3001');

function formatPrice(price) {
  if (typeof price !== 'number') return '';
  return price % 1 === 0 
    ? `$${price}` 
    : `$${price.toFixed(2)}`;
}

function calculatePainMetric(price, distance) {
  if (typeof price !== 'number' || typeof distance !== 'number') {
    return Infinity;
  }
  return price * distance;
}

import StoreModal from './StoreModal';

// Memoized ProductItem for performance


import { Suspense } from 'react';
// Import directly instead of lazy loading to avoid chunk loading issues in embedded mode
import ExpandedProductCard from './ExpandedProductCard';
import HamsterLoader from './HamsterLoader';

// In-memory cache for retailer products to avoid repeat fetches on re-expand
const retailerProductsCache = new Map(); // key: retailerId, value: { ts: number, products: any[] }
const RETAILER_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const ProductItem = React.memo(React.forwardRef(function ProductItem({
  product,
  store,
  config,
  onSale,
  sortBy,
  painMetric,
  displayName,
  hasValidUrl,
  canonicalProduct,
  expandedCardId,
  setExpandedCardId,
  allProducts,
  setGlobalLoaderState,
  globalLoaderState,
  onScrollSiblingIntoView, // callback to scroll sibling card
  productIndex,           // index of this product in the visible list
  visibleProductsLength,  // total visible products
  ...props
}, ref) {
  // Use parent component's loader state
  const loaderTimeoutRef = React.useRef();

  // Get image path directly from canonical product
  const imagePath = useMemo(() => getProductImagePath(canonicalProduct), [canonicalProduct]);

  // Determine stock indicator type
  let stockIndicator = 'in-stock';
  if (config?.currentStock <= 0) stockIndicator = 'out-of-stock';
  else if (config?.currentStock <= 5) stockIndicator = 'low-stock';

  // Split product information for display
  const productBrand = canonicalProduct?.brand || '';
  const productDisplayName = canonicalProduct?.displayName || displayName;
  const productTHC = canonicalProduct?.thc || '';

  // Handle store click to expand/collapse card - Optimized for better INP
  const handleStoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear any existing timeout to prevent multiple timeouts
    if (loaderTimeoutRef.current) {
      clearTimeout(loaderTimeoutRef.current);
      loaderTimeoutRef.current = null;
    }
    
    // If we're already showing the expanded card for this product, just close it
    if (expandedCardId === product.id) {
      console.debug('[ProductItem] Closing expanded card for product', product.id);
      setExpandedCardId(null);
      // NEW: After closing, scroll sibling card into view
      setTimeout(() => {
        if (onScrollSiblingIntoView) {
          onScrollSiblingIntoView(productIndex, visibleProductsLength);
        }
      }, 80); // Wait for collapse animation
      return;
    }
    
    // STEP 1: CRITICAL VISUAL UPDATES - Do these immediately for better INP
    // Show loader instantly
    setGlobalLoaderState({
      isLoading: true,
      productId: product.id,
      storeName: store.name
    });
    
    // If another card is expanded, collapse it immediately
    if (expandedCardId !== null) {
      setExpandedCardId(null);
    }
    
    // STEP 2: SCHEDULE VISUAL UPDATES - Use rAF to ensure loader is painted before expanding card
    requestAnimationFrame(() => {
      // Schedule expanding the card - this is the critical visual update
      loaderTimeoutRef.current = setTimeout(() => {
        console.debug('[ProductItem] Loader timeout done, showing expanded card for product', product.id);
        setGlobalLoaderState({
          isLoading: false,
          productId: null,
          storeName: ''
        });
        setExpandedCardId(product.id);
        
        // Wait for expanded card to render, then scroll it into view smoothly
        setTimeout(() => {
          if (ref && ref.current && typeof ref.current.scrollIntoView === 'function') {
            ref.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest',
              inline: 'nearest'
            });
          } else {
            const el = document.querySelector('.expanded-card-wrapper');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        }, 50);
      }, 100); // 100ms loader for snappy responsiveness
      
      // STEP 3: DEFER NON-CRITICAL WORK - Move analytics tracking to the next task
      // This prevents it from blocking the main thread during the interaction
      setTimeout(() => {
        // Track the event when someone opens the expanded card as visiting a store stock page
        const storeForTracking = {
          name: store.name,
          id: product.retailerId,
          city: store.city || '',
          state: store.state || ''
        };
        
        // Enhance product data with canonical information if available
        const enhancedProduct = {
          ...product,
          displayName: productDisplayName,
          canonicalName: canonicalProduct?.canonicalName || '',
          brand: productBrand,
          category: canonicalProduct?.category || ''
        };
        
        // This tracking doesn't affect the visual update, so defer it
        trackProductDetails(enhancedProduct, storeForTracking);
        console.debug('[ProductItem] Analytics tracking completed for', product.id);
      }, 0);
    });
  };

  // Cleanup loader timeout and track expanded state changes - optimized for performance
  React.useEffect(() => {
    // Cleanup function that will run on unmount or when expandedCardId changes
    return () => {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
        loaderTimeoutRef.current = null;
      }
    };
  }, [expandedCardId]); // Re-create cleanup when expandedCardId changes

  // Find other products at this store (excluding this product) - now uses optimized backend endpoint
  // ONLY fetch when this card is actually expanded to avoid unnecessary API calls
  const [otherProducts, setOtherProducts] = React.useState([]);
  React.useEffect(() => {
    // Only fetch retailer products if THIS card is expanded
    if (expandedCardId !== product.id || !product.retailerId) {
      setOtherProducts([]); // Clear products if not expanded
      return;
    }
    
    // AbortController to cancel stale requests
    const controller = new AbortController();
    const { signal } = controller;

    // Use the backend endpoint for instant results with client-side cache
    const fetchRetailerProducts = async () => {
      try {
        const now = Date.now();
        const cached = retailerProductsCache.get(product.retailerId);

        let baseProducts;
        if (cached && (now - cached.ts) < RETAILER_CACHE_TTL_MS) {
          baseProducts = cached.products;
        } else {
          // Fetch full retailer catalog (no exclude) for cache reuse
          const url = `${API_BASE_URL}/api/products/by-retailer/${product.retailerId}`;
          const response = await authenticatedFetch(url, { signal });
          if (!response.ok) throw new Error(`API error: ${response.status}`);
          const data = await response.json();
          baseProducts = data.data || [];
          retailerProductsCache.set(product.retailerId, { ts: now, products: baseProducts });
        }

        // Exclude current product locally
        const filtered = baseProducts.filter(p => p.id !== product.id);

        // Add canonical product matching, onSale status, and onViewDetails handlers
        const productsWithHandlers = filtered.map(otherProduct => {
          // Get the first config for this product
          const otherConfig = otherProduct.configs?.[0];
          
          // Determine if on sale
          const isOnSale = otherConfig && otherConfig.salePrice && otherConfig.salePrice < otherConfig.normalPrice;
          
          // Get canonical product info for matching
          const otherCanonical = getCanonicalProductSync(otherProduct.name) || null;
          
          // Check if has valid URL - check URLs directly or use config to determine menu type
          let hasValidProductUrl = false;
          const hasValidRecUrl = otherProduct.recreationalURL && otherProduct.recreationalURL.startsWith('http');
          const hasValidMedUrl = otherProduct.medicalURL && otherProduct.medicalURL.startsWith('http');
          
          // If config indicates recreational, use rec URL; if medical, use med URL; otherwise use any available
          if (otherConfig?.recreational && hasValidRecUrl) {
            hasValidProductUrl = true;
          } else if (otherConfig?.medical && hasValidMedUrl) {
            hasValidProductUrl = true;
          } else if (hasValidRecUrl || hasValidMedUrl) {
            hasValidProductUrl = true; // Use any available valid URL
          }
          
          return {
            ...otherProduct,
            canonicalProduct: otherCanonical,
            onSale: isOnSale,
            hasValidUrl: hasValidProductUrl,
            onViewDetails: (e) => {
            if (e) { e.preventDefault(); e.stopPropagation(); }

            const hasValid = (url) => typeof url === 'string' && url.startsWith('http');
            let productUrl = null;
            
            // Use config to determine which URL to prefer, or use any available valid URL
            if (otherConfig?.recreational && hasValid(otherProduct.recreationalURL)) {
              productUrl = otherProduct.recreationalURL;
            } else if (otherConfig?.medical && hasValid(otherProduct.medicalURL)) {
              productUrl = otherProduct.medicalURL;
            } else if (hasValid(otherProduct.recreationalURL)) {
              productUrl = otherProduct.recreationalURL;
            } else if (hasValid(otherProduct.medicalURL)) {
              productUrl = otherProduct.medicalURL;
            }
            
            if (productUrl) {
              window.open(productUrl, '_blank', 'noopener,noreferrer');
            } else {
              console.warn('No valid product URL available for other product', otherProduct?.id);
            }
          }
          };
        });

        setOtherProducts(productsWithHandlers);
      } catch (error) {
        if (error?.name === 'AbortError') return; // ignore
        console.error('Error fetching retailer products:', error);
        setOtherProducts([]);
      }
    };

    fetchRetailerProducts();

    // Cleanup abort on unmount / dependency change
    return () => controller.abort();
  }, [product.retailerId, product.id, expandedCardId]); // Added expandedCardId dependency

  // Product URL for expanded card
  const productUrl = product.menuTypes?.includes('recreational') ? product.recreationalURL : product.medicalURL;

  // Render expanded card if this is the expanded one (lazy + Suspense)
  if (expandedCardId === product.id) {
    return (
      <Suspense fallback={<div style={{padding:40, textAlign:'center'}}>Loading details...</div>}>
        <div 
          className="expanded-card-wrapper"
          ref={ref}
          style={{ 
            contentVisibility: 'auto', 
            containIntrinsicSize: '600px',
            gridColumn: '1 / -1', // Make the card span all columns
            width: '100%',        // Take full width
            maxWidth: '100%',     // Full width in parent container
            margin: '0',          // No margins
            zIndex: 10,           // Ensure it's above other cards
            position: 'relative', // Establish stacking context
            padding: 0            // No padding
          }}
        >
          <ExpandedProductCard
            product={{ ...product, imagePath }}
            store={store}
            config={config}
            canonicalProduct={canonicalProduct}
            onSale={onSale}
            hasValidUrl={hasValidUrl}
            productUrl={productUrl}
            otherProducts={otherProducts}
            onClose={() => setExpandedCardId(null)}
            distance={store.distance}
            city={store.city}
            state={store.state}
          />
        </div>
      </Suspense>
    );
  }

  // Default: collapsed card
  return (
    <div
      className="card clean-design"
      style={{ contentVisibility: 'auto', containIntrinsicSize: '400px' }}
      ref={ref}
      data-product-id={product.id}
    >
      {/* Store logo and name in top left corner */}
      <div
        className="store-info-corner"
        onClick={handleStoreClick}
        style={{ cursor: 'pointer' }}
        title="Click to expand and view store details"
      >
        {store.imageUrl && (
          <img
            src={store.imageUrl}
            alt={store.name}
            className="store-image-corner"
          />
        )}
        <span className="store-name-corner">{store.name}</span>
      </div>
      
      {/* Card-specific loader shown only when needed (unmounted when not loading) */}
      {globalLoaderState.isLoading && globalLoaderState.productId === product.id && (
        <div 
          className="card-hamster-loader" 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            zIndex: 900,
            borderRadius: '12px',
            backdropFilter: 'blur(2px)',
            boxShadow: 'inset 0 0 0 2px #1976d2',
            opacity: 1,
            pointerEvents: 'auto',
            transition: 'opacity 0.15s ease-out, box-shadow 0.15s ease-out',
            willChange: 'opacity, box-shadow'
          }}
        >
          <HamsterLoader 
            size="small" 
            speed="normal" 
            label={`Loading ${store.name || 'store'} details...`}
          />
          <div style={{ 
            marginTop: '10px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#1976d2'
          }}>
            Loading details...
          </div>
        </div>
      )}

      {/* Medical/Recreational indicators - Now mutually exclusive */}
      {product.menuTypes?.includes('medical') && (
        <div className="menu-type-indicator medical">MED</div>
      )}
      {product.menuTypes?.includes('recreational') && (
        <div className="menu-type-indicator recreational">REC</div>
      )}

      <div className="bg">
        {/* Large Flavor Image at the top - now with 3D video for mobile */}
        <div className="product-hero-image">
          <MobileProductVideo
            product={product}
            canonicalProduct={canonicalProduct}
            imagePath={imagePath}
          />
        </div>

        {/* Product Name - Large and bold */}
        <h3 className="product-name">{productDisplayName}</h3>

        {/* Product subtitle with brand and THC info */}
        <div className="product-subtitle">
          {productBrand && productTHC && `${productBrand} • ${productTHC}`}
        </div>

        {/* Product Price - Prominent */}
        <div className="product-price">
          {onSale ? (
            <>
              <span className="sale-price">{formatPrice(config.salePrice)}</span>
              <span className="original-price">{formatPrice(config.normalPrice)}</span>
            </>
          ) : (
            <span className="regular-price">{formatPrice(config?.normalPrice)}</span>
          )}
        </div>

        {/* Stock and Distance Info - Combined */}
        <div className="product-meta">
          <div className="stock-distance-info">
            <span className={`stock-info ${stockIndicator === 'low-stock' ? 'low-stock-text' : ''}`}>
              {!config ? (
                'Stock unknown'
              ) : config.currentStock <= 0 ? (
                'Out of Stock'
              ) : config.currentStock <= 5 ? (
                `${config.currentStock} left`
              ) : (
                `${config.currentStock > 20 ? '20+' : config.currentStock} left`
              )}
            </span>
            <span className="separator">|</span>
            <span className="distance-info">
              {store.distance ? `${Math.round(store.distance)} mi` : `${store.city}, ${store.state}`}
            </span>
          </div>
        </div>

        {/* Action Button - Use appropriate URL based on menu type */}
        {hasValidUrl ? (
          <a
            href={productUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="view-details-button"
            onClick={(e) => {
              const storeForTracking = {
                fields: {
                  Name: store.name,
                  City: store.city || '',
                  State: store.state || ''
                }
              };
              trackProductView(product, storeForTracking, canonicalProduct);
            }}
            style={{
              background: '#1976d2',
              color: '#fff',
              fontWeight: '700',
              padding: '10px 24px',
              borderRadius: '10px',
              border: 'none',
              fontSize: '15px',
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-block',
              textAlign: 'center',
              boxShadow: 'none'
            }}
          >
            View details
          </a>
        ) : (
          <span
            className="view-details-button disabled"
            title="This product does not have a valid URL"
            style={{
              background: '#eee',
              color: '#aaa',
              fontWeight: '700',
              padding: '10px 24px',
              borderRadius: '10px',
              border: 'none',
              fontSize: '15px',
              cursor: 'not-allowed',
              textDecoration: 'none',
              display: 'inline-block',
              textAlign: 'center',
              boxShadow: 'none'
            }}
          >
            Link Unavailable
          </span>
        )}
      </div>

      {/* Stock indicator band at the bottom */}
      {(stockIndicator === 'low-stock' || stockIndicator === 'out-of-stock') && (
        <div className="card-stock-indicator" data-stock={stockIndicator}>
          {stockIndicator === 'low-stock' ? 'LOW STOCK' : 'OUT OF STOCK'}
        </div>
      )}
    </div>
  );
}));

ProductItem.propTypes = {
  product: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  config: PropTypes.object,
  onSale: PropTypes.bool,
  sortBy: PropTypes.string,
  painMetric: PropTypes.number,
  displayName: PropTypes.string,
  hasValidUrl: PropTypes.bool,
  canonicalProduct: PropTypes.object
};

const ProductSearch = ({ shopperLocation, initialSearchTerm = '' }) => {
  // Refs for all visible product cards
  const cardRefs = useRef([]);
  // Guard against repeat triggers for load-more
  const lastTriggeredCountRef = useRef(0);
  // State for expanded card
  const [expandedCardId, setExpandedCardId] = useState(null);
  // State for products and filtering
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [selectedCanonicalNames, setSelectedCanonicalNames] = useState([]); // Only canonical names now
  const [storeInfo, setStoreInfo] = useState({});
  const [sortBy, setSortBy] = useState('pain-asc');
  const [errorState, setErrorState] = useState({ hasError: false, message: '' });
  const [canonicalProductCache, setCanonicalProductCache] = useState(new Map());
  
  // Global loader state
  const [globalLoaderState, setGlobalLoaderState] = useState({
    isLoading: false,
    productId: null,
    storeName: ''
  });
  
  // Debug when global loader state changes
  useEffect(() => {
    console.debug(`[ProductSearch] Global loader state changed:`, globalLoaderState);
  }, [globalLoaderState]);
  
  // Expose filteredProducts to window for the StoreModal
  useEffect(() => {
    // Create or update the global context object
    window.productSearchContext = window.productSearchContext || {};
    window.productSearchContext.filteredProducts = filteredProducts;
    
    return () => {
      // Clean up when component unmounts
      if (window.productSearchContext) {
        window.productSearchContext.filteredProducts = null;
      }
    };
  }, [filteredProducts]);
  
  // Track all available products for the StoreModal to show all products regardless of filters
  const [allProducts, setAllProducts] = useState([]);
  
  // Fetch all products for store modals
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const url = `${API_BASE_URL}/api/products/cheapest?limit=1000`;
        const response = await authenticatedFetch(url);
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        const data = await response.json();
        
        // Store all products for StoreModal use
        setAllProducts(data.data || []);
        
        // Make available to window context
        window.productSearchContext = window.productSearchContext || {};
        window.productSearchContext.allProducts = data.data || [];
      } catch (error) {
        console.error('Error fetching all products for store modals:', error);
      }
    };
    
    fetchAllProducts();
    
    return () => {
      // Clean up
      if (window.productSearchContext) {
        window.productSearchContext.allProducts = null;
      }
    };
  }, []);
  
  // State for new filter functionality
  const [productFilters, setProductFilters] = useState({
    thcAmount: [],
    cbdAmount: [],
    menuType: [],
    stockFilter: []
  });
  
  // State for pagination
  const [displayCount, setDisplayCount] = useState(20);
  
  // State for showing grid or results
  const [showGrid, setShowGrid] = useState(!initialSearchTerm);

  // Batch size before requiring explicit user action (20 on mobile, 60 on desktop)
  const batchSize = useMemo(() => {
    if (typeof window === 'undefined') return 60;
    return window.innerWidth < 768 ? 20 : 60;
  }, []);
  // Upper limit for IO-driven autoloading; clicking the button advances this by one batch
  const [autoLoadLimit, setAutoLoadLimit] = useState(0);
  useEffect(() => {
    // Initialize/Reset autoload limit when starting a new list segment
    if (displayCount <= batchSize) {
      setAutoLoadLimit(batchSize);
    }
  }, [batchSize, displayCount]);

  // Sort products based on selected criteria (defined before first use)
  const sortProductsWithStoreInfo = useCallback((products) => {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return [];
    }
    // Only include products with known store info
    const productsWithStoreInfo = products.filter(product =>
      product && product.retailerId && storeInfo[product.retailerId]
    );

    if (productsWithStoreInfo.length === 0) return [];

    const enhancedProducts = productsWithStoreInfo.map(product => {
      const store = storeInfo[product.retailerId];
      const price = product.configs?.[0]?.salePrice || product.configs?.[0]?.normalPrice || 0;
      const distance = store.distance || 999999;
      const painMetric = calculatePainMetric(price, distance);
      return { ...product, _sortData: { price, distance, painMetric } };
    });

    switch (sortBy) {
      case 'price-asc':
        enhancedProducts.sort((a, b) => a._sortData.price - b._sortData.price);
        break;
      case 'price-desc':
        enhancedProducts.sort((a, b) => b._sortData.price - a._sortData.price);
        break;
      case 'distance-asc':
        enhancedProducts.sort((a, b) => a._sortData.distance - b._sortData.distance);
        break;
      case 'pain-asc':
        enhancedProducts.sort((a, b) => a._sortData.painMetric - b._sortData.painMetric);
        break;
      default:
        enhancedProducts.sort((a, b) => a._sortData.price - b._sortData.price);
    }

    return enhancedProducts;
  }, [storeInfo, sortBy]);

  // NEW: Memoized datasets for rendering and observer logic
  const sortedProducts = useMemo(() => {
    return sortProductsWithStoreInfo(filteredProducts);
  }, [filteredProducts, sortProductsWithStoreInfo]);

  const visibleProducts = useMemo(() => {
    return sortedProducts.slice(0, displayCount);
  }, [sortedProducts, displayCount]);

  const hasMore = useMemo(() => {
    return displayCount < sortedProducts.length;
  }, [displayCount, sortedProducts.length]);

  // Pause auto-loading when we hit a batch boundary (e.g., 60, 120, ...)
  const pauseAutoLoad = useMemo(() => {
    if (!hasMore) return false;
    return displayCount >= autoLoadLimit;
  }, [displayCount, hasMore, autoLoadLimit]);

  // Ensure the last-triggered count guard resets when displayCount is reduced
  useEffect(() => {
    if (displayCount < lastTriggeredCountRef.current) {
      lastTriggeredCountRef.current = displayCount;
    }
  }, [displayCount]);

  // Make sure to clear the global loader when component unmounts or on any critical changes
  useEffect(() => {
    // Function to clear the global loader
    const clearGlobalLoader = () => {
      setGlobalLoaderState({
        isLoading: false,
        productId: null,
        storeName: ''
      });
    };
    
    // Clear on unmount
    return clearGlobalLoader;
  }, []);

  // Load canonical products for grid
  const [canonicalProducts, setCanonicalProducts] = useState([]);
  useEffect(() => {
    // Get the base URL for assets (either from environment or default to CDN)
    const baseUrl = import.meta.env.BASE_URL || 
                   process.env.REACT_APP_BASE_URL || 
                   window.STORE_LOCATOR_CONFIG?.CDN_URL || 
                   'https://ddeewr0txvw1f.cloudfront.net';
    const cdnUrl = 'https://ddeewr0txvw1f.cloudfront.net'; // Direct CDN URL for fallback

    // Fetch canonical products from the JSON file with the correct base URL
    const loadCanonicalProducts = async () => {
      try {
        // First try with the configured base URL
        const response = await fetch(`${baseUrl}/canonical-products.json`);
        if (!response.ok) {
          console.warn(`Failed to load canonical products from ${baseUrl}, falling back to CDN URL`);
          // If that fails, try the CDN URL directly
          const cdnResponse = await fetch(`${cdnUrl}/canonical-products.json`);
          if (!cdnResponse.ok) {
            throw new Error(`Failed to load canonical products: ${cdnResponse.status} ${cdnResponse.statusText}`);
          }
          return cdnResponse.json();
        }
        return response.json();
      } catch (error) {
        console.error('Error loading canonical products:', error);
        // Try one more time with CDN URL as a last resort
        const lastResortResponse = await fetch(`${cdnUrl}/canonical-products.json`);
        return lastResortResponse.json();
      }
    };

    loadCanonicalProducts()
      .then(data => {
        setCanonicalProducts(data.products.map(p => ({
          ...p,
          imagePath: p.imagePath || getProductImagePath(p)
        })));
      })
      .catch(error => {
        console.error('Error loading canonical products:', error);
      });
  }, []);

  // --- Client-side search cache ---
  const searchCache = useRef({});
  
  // Track API calls to prevent infinite loops
  const apiCallsRef = useRef(new Set());
  
  // Function to get canonical product info with caching
  const getCachedCanonicalProduct = useCallback(async (productName, price) => {
    const cacheKey = `${productName}-${price}`;
    
    // First check cache
    if (canonicalProductCache.has(cacheKey)) {
      return canonicalProductCache.get(cacheKey);
    }
    
    // Then try synchronous lookup for immediate results
    const syncProduct = getCanonicalProductSync(productName);
    if (syncProduct) {
      setCanonicalProductCache(prev => new Map(prev.set(cacheKey, syncProduct)));
      return syncProduct;
    }
    
    // Finally fall back to async lookup
    try {
      const canonical = await getCanonicalProductInfo(productName, price);
      setCanonicalProductCache(prev => new Map(prev.set(cacheKey, canonical)));
      return canonical;
    } catch (error) {
      console.error('Error getting canonical product info:', error);
      return null;
    }
  }, [canonicalProductCache]);
  
  // Pre-compute canonical products for visible products
  useEffect(() => {
    const preComputeCanonicalProducts = async () => {
      const promises = filteredProducts.slice(0, displayCount).map(async (product) => {
        const config = product.configs?.[0];
        const price = config?.salePrice || config?.normalPrice || 0;
        return getCachedCanonicalProduct(product.name, price);
      });
      
      await Promise.all(promises);
    };
    
    if (filteredProducts.length > 0) {
      preComputeCanonicalProducts();
    }
  }, [filteredProducts, displayCount, getCachedCanonicalProduct]);

  // Distance calculation function
  const calculateDistance = (pos1, pos2) => {
    if (!pos1 || !pos2 || !pos1.lat || !pos2.lat) return null;
    
    const toRad = (value) => value * Math.PI / 180;
    const lat1 = parseFloat(pos1.lat);
    const lat2 = parseFloat(pos2.lat);
    const lon1 = parseFloat(pos1.lng);
    const lon2 = parseFloat(pos2.lng);
    
    if (isNaN(lat1) || isNaN(lat2) || isNaN(lon1) || isNaN(lon2)) return null;
    
    const radlat1 = toRad(lat1);
    const radlat2 = toRad(lat2);
    const theta = lon1 - lon2;
    const radtheta = toRad(theta);
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + 
               Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    if (dist > 1) dist = 1;
    dist = Math.acos(dist);
    dist = dist * 180 / Math.PI;
    dist = dist * 60 * 1.1515;
    return dist;
  };

  // (moved) sortProductsWithStoreInfo defined earlier before first use



  // Fetch store locations to get distance information
  const fetchStoreLocations = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        fields: "fields[]=Name&fields[]=DeliveryURL&fields[]=PickupURL&fields[]=City&fields[]=State&fields[]=Lat&fields[]=Lng&fields[]=DeliveryAreasLookup&fields[]=Image&fields[]=LitAlertsId",
        filter: 'AND({Pickup}=1,{Visible}=1)'
      });
      
      const response = await authenticatedFetch(`${API_BASE_URL}/api/locations?${params}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      
      const storeMap = {};
      if (data.records) {
        data.records.forEach(store => {
          if (store.fields.LitAlertsId) {
            const storeId = parseInt(store.fields.LitAlertsId, 10);
            let distance = null;
            
            if (shopperLocation && shopperLocation.lat && shopperLocation.lng && 
                store.fields.Lat && store.fields.Lng) {
              distance = calculateDistance(
                { lat: parseFloat(shopperLocation.lat), lng: parseFloat(shopperLocation.lng) },
                { lat: parseFloat(store.fields.Lat), lng: parseFloat(store.fields.Lng) }
              );
            }
            
            storeMap[storeId] = {
              name: store.fields.Name,
              city: store.fields.City,
              state: store.fields.State,
              distance: distance,
              imageUrl: store.fields.Image?.[0]?.thumbnails?.small?.url
            };
          }
        });
      }
      
      setStoreInfo(storeMap);
    } catch (error) {
      console.error('Error fetching store locations:', error);
    }
  }, [shopperLocation]);

  // Apply client-side filters to products (optimized for performance)
  const applyProductFilters = useCallback((products) => {
    if (!products || products.length === 0) return products;
    
    // Early return if no filters are active
    const hasFilters = productFilters.thcAmount.length > 0 || 
                      productFilters.cbdAmount.length > 0 || 
                      productFilters.menuType.length > 0 ||
                      productFilters.stockFilter.length > 0;
    
    if (!hasFilters) return products;
    
    // Use fast string matching instead of canonical lookup for performance
    if (products.length > 500 && process.env.NODE_ENV === 'development') {
      console.log('⚡ Using optimized filtering for large dataset:', products.length, 'products');
    }
    
    return products.filter(product => {
      // THC Amount filtering - fast string matching
      if (productFilters.thcAmount.length > 0) {
        const productName = product.name.toLowerCase();
        const matchesTHC = productFilters.thcAmount.some(amount => {
          // Fast string matching instead of canonical lookup
          switch (amount) {
            case '150': return productName.includes('150') || productName.includes('125');
            case '300': return productName.includes('300');
            case '500': return productName.includes('500');
            case '1000': return productName.includes('1000') || productName.includes('1g');
            default: return false;
          }
        });
        if (!matchesTHC) return false;
      }
      
      // CBD Amount filtering - fast string matching
      if (productFilters.cbdAmount.length > 0) {
        const productName = product.name.toLowerCase();
        const matchesCBD = productFilters.cbdAmount.some(amount => {
          // Fast string matching for CBD products
          switch (amount) {
            case '300': return productName.includes('300') && (productName.includes('cbd') || productName.includes('1:1'));
            case '500': return productName.includes('500') && (productName.includes('cbd') || productName.includes('1:1'));
            default: return false;
          }
        });
        if (!matchesCBD) return false;
      }
      
      // Menu Type filtering (Recreational vs Medical) - ready for backend changes
      if (productFilters.menuType.length > 0) {
        const hasRec = productFilters.menuType.includes('rec');
        const hasMed = productFilters.menuType.includes('medical');
        
        if (hasRec && hasMed) {
          return true; // Show all if both are selected
        } else if (hasRec) {
          return !!product.recreationalURL; // Show if has recreational URL
        } else if (hasMed) {
          return !!product.medicalURL; // Show if has medical URL
        }
      }
      
      // Stock filtering - only show in-stock products
      if (productFilters.stockFilter.includes('inStockOnly')) {
        // Check if product has configs with stock information
        if (!product.configs || product.configs.length === 0) {
          return false; // No configs means no stock info
        }
        
        // Check if the first config has stock > 0
        return product.configs[0].currentStock > 0;
      }
      
      return true;
    });
  }, [productFilters]);

  // --- Fetch filtered products - OPTIMIZED with backend menu type support ---
  const fetchFilteredProducts = useCallback(async (canonicalNamesOverride = null) => {
    const canonicalNamesToUse = canonicalNamesOverride !== null ? canonicalNamesOverride : selectedCanonicalNames;
    
    // Create unique API call ID to prevent duplicates - include filters in the ID
    const filterKey = `${productFilters.thcAmount.join(',')}_${productFilters.cbdAmount.join(',')}_${productFilters.menuType.join(',')}_${productFilters.stockFilter.join(',')}`;
    const callId = `${canonicalNamesToUse.join(',')}|${sortBy}|${filterKey}`;
    if (apiCallsRef.current.has(callId)) {
      if (process.env.NODE_ENV === 'development') { 
        console.log('🚫 Preventing duplicate API call:', callId);
      }
      return;
    }
    
    setIsFiltering(true);
    setDisplayCount(20);
    setErrorState({ hasError: false, message: '' });

    // Check cache first - include filters in cache key
    const cacheKey = `${canonicalNamesToUse.join(',')}|${sortBy}|${filterKey}`;
    if (searchCache.current[cacheKey]) {
      if (process.env.NODE_ENV === 'development') { 
        console.log('Using cached results for:', cacheKey);
      }
      setFilteredProducts(searchCache.current[cacheKey]);
      setIsFiltering(false);
      setIsLoading(false);
      return;
    }

    // Add to active calls
    apiCallsRef.current.add(callId);

    try {
      let url = `${API_BASE_URL}/api/products/cheapest?limit=1000`;
      
      // Add canonical names to URL
      if (canonicalNamesToUse.length > 0) {
        canonicalNamesToUse.forEach(name => {
          url += `&canonicalNames[]=${encodeURIComponent(name)}`;
        });
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetching products for canonical names:', canonicalNamesToUse);
        }
      }
      
      // Add menu type filtering to backend request if available
      if (productFilters.menuType.length > 0) {
        productFilters.menuType.forEach(menuType => {
          url += `&menuTypes[]=${encodeURIComponent(menuType)}`;
        });
      }
      
      // Add stock filter to backend request if available
      if (productFilters.stockFilter.length > 0) {
        productFilters.stockFilter.forEach(filter => {
          url += `&stockFilter[]=${encodeURIComponent(filter)}`;
        });
      }
      if (process.env.NODE_ENV === 'development') {
        console.log('API URL:', url);
        console.log('📊 Calling API with filters:', { 
          canonicalNamesToUse, 
          menuTypes: productFilters.menuType,
          stockFilter: productFilters.stockFilter
        });
      }
      
      const response = await authenticatedFetch(url);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      if (process.env.NODE_ENV === 'development') { 
        console.log('✅ API Response - received', data.data?.length || 0, 'products');
      }
      
      // Apply remaining client-side filters (THC/CBD amounts since backend doesn't support these yet)
      const rawProducts = data.data || [];
      const filteredResults = applyProductFilters(rawProducts);
      
      if (process.env.NODE_ENV === 'development') { 
        console.log(`🔍 After client-side filtering: ${filteredResults.length} of ${rawProducts.length} products`);
      }
      
      // Cache and use results
      searchCache.current[cacheKey] = filteredResults;
      setFilteredProducts(filteredResults);
    } catch (error) {
      setErrorState({
        hasError: true,
        message: 'Unable to load product results. Please try again later.'
      });
      setFilteredProducts([]);
      console.error('Error fetching filtered products:', error);
    } finally {
      setIsFiltering(false);
      setIsLoading(false);
      // Remove from active calls after a delay
      setTimeout(() => {
        apiCallsRef.current.delete(callId);
      }, 1000);
    }
  }, [selectedCanonicalNames, sortBy, productFilters, applyProductFilters]);

  // --- Fetch all products first - simplified ---
  const fetchAllProducts = useCallback(() => {
    setIsLoading(true);
    fetchFilteredProducts([]);  // Pass empty array for all products
  }, [fetchFilteredProducts]);

  // Update URL with search parameters - SIMPLIFIED for dropdown only
  const updateUrlWithSearch = useCallback((canonicalNames = [], sortOption = sortBy) => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      
      // Set tab parameter to product-search
      url.searchParams.set('tab', 'product-search');
      
      // Clear all existing parameters
      url.searchParams.delete('search');
      url.searchParams.delete('products');
      url.searchParams.delete('sort');
      
      // Manually construct the URL to ensure spaces are encoded as %20, not +
      let finalUrl = url.origin + url.pathname + '?tab=product-search';
      
      // Add canonical names parameter with proper encoding
      if (canonicalNames && canonicalNames.length > 0) {
        const encodedProducts = canonicalNames
          .map(name => encodeURIComponent(name))
          .join(',');
        finalUrl += `&products=${encodedProducts}`;
      }
      
      // Add sort parameter if not the default
      if (sortOption && sortOption !== 'price-asc') {
        finalUrl += `&sort=${encodeURIComponent(sortOption)}`;
      }
      
      // Update the URL without refreshing the page
      window.history.replaceState({
        products: canonicalNames,
        sort: sortOption
      }, '', finalUrl);
      if (process.env.NODE_ENV === 'development') {  
        console.log('URL updated:', finalUrl);
      }
    }
  }, [sortBy]);

  // Handle browser back/forward navigation - SIMPLIFIED for dropdown only
  useEffect(() => {
    const handlePopState = (event) => {
      if (process.env.NODE_ENV === 'development') { 
        console.log('PopState event triggered');
      }
      const urlParams = new URLSearchParams(window.location.search);
      const productsParam = urlParams.get('products') || '';
      const sortParam = urlParams.get('sort') || 'price-asc';
      if (process.env.NODE_ENV === 'development') { 
        console.log('URL params:', { productsParam, sortParam });
      }
      
      // Parse canonical names from products parameter - properly decode %20 encoded spaces
      const canonicalNames = productsParam ? 
        productsParam.split(',')
          .map(name => decodeURIComponent(name.trim()))
          .filter(name => name) : [];
      
      // Update state
      setSelectedCanonicalNames(canonicalNames);
      setSortBy(sortParam);
      
      // Show grid if no products are selected, otherwise show search results
      if (canonicalNames.length === 0) {
        setShowGrid(true);
        setFilteredProducts([]);
      } else {
        setShowGrid(false);
        // Clear cache and trigger search
        searchCache.current = {};
        apiCallsRef.current.clear();
        setDisplayCount(20);
        
        // Trigger search with parsed parameters
        fetchFilteredProducts(canonicalNames);
      }
    };
    
    // Initial URL parameter parsing on mount
    const parseInitialUrl = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const productsParam = urlParams.get('products') || '';
      const sortParam = urlParams.get('sort') || 'price-asc';

      if (process.env.NODE_ENV === 'development') {
        console.log('Initial URL params:', { productsParam, sortParam });
      }
      
      // Parse canonical names - properly decode %20 encoded spaces
      const canonicalNames = productsParam ? 
        productsParam.split(',')
          .map(name => decodeURIComponent(name.trim()))
          .filter(name => name) : [];
      
      // Update state if we have URL parameters
      if (productsParam || sortParam !== 'price-asc') {
        setSelectedCanonicalNames(canonicalNames);
        setSortBy(sortParam);
        setShowGrid(false); // Hide grid when we have search parameters
        
        // Trigger search with the parsed parameters
        fetchFilteredProducts(canonicalNames);
      } else {
        // No URL parameters, show the grid
        setShowGrid(true);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // Parse initial URL on component mount
    parseInitialUrl();
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Remove fetchFilteredProducts from dependencies to prevent infinite loop

  // Load products on component mount - SIMPLIFIED
  useEffect(() => {
    // Only load all products if we don't have URL parameters to process
    const urlParams = new URLSearchParams(window.location.search);
    const hasUrlParams = urlParams.get('products');
    
    if (!hasUrlParams) {
      fetchAllProducts();
    }
  }, [fetchAllProducts]);
  
  // Update store locations when the shopper location changes
  useEffect(() => {
    fetchStoreLocations();
  }, [fetchStoreLocations]);
  
  // Re-filter when product filters change
  useEffect(() => {
    // Only re-filter if we have results to filter and filters actually changed
    if (selectedCanonicalNames.length > 0 || Object.values(productFilters).some(arr => arr.length > 0)) {
      // Clear cache to force re-filtering
      searchCache.current = {};
      apiCallsRef.current.clear();
      
      // Re-fetch with current selections and filters
      fetchFilteredProducts(selectedCanonicalNames);
    }
  }, [productFilters.thcAmount, productFilters.cbdAmount, productFilters.menuType, productFilters.stockFilter]);
  
  // Remove old window scroll pagination and replace with IntersectionObserver based pagination
  // useEffect(() => { ...old scroll handler... }, [isLoading, isFiltering, showGrid]);

  // IntersectionObserver: watch the last visible product card.
  // When it enters the viewport, load more products.
  useEffect(() => {
    if (showGrid || !hasMore) return;
    if (isLoading || isFiltering) return;
    if (pauseAutoLoad) return; // stop auto-loading at batch boundaries

    const lastIndex = Math.max(0, visibleProducts.length - 1);
    const targetEl = cardRefs.current[lastIndex];
    if (!targetEl) return;

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!hasMore) continue;
        const increment = window.innerWidth < 768 ? 10 : 20;
        const newCount = Math.min(displayCount + increment, autoLoadLimit, sortedProducts.length);
        if (newCount !== displayCount && newCount !== lastTriggeredCountRef.current) {
          lastTriggeredCountRef.current = newCount;
          try { observer.unobserve(entry.target); } catch (e) {}
          setDisplayCount(newCount);
        }
      }
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0
    });

    observer.observe(targetEl);

    return () => {
      observer.disconnect();
    };
  }, [visibleProducts.length, showGrid, hasMore, isLoading, isFiltering, displayCount, sortedProducts.length, pauseAutoLoad]);

  // Handler for explicit "Load more" button
  const handleLoadMoreClick = useCallback(() => {
    setAutoLoadLimit(prev => {
      const next = Math.min(prev + batchSize, sortedProducts.length);
      // When resuming, ensure the observer can immediately continue from the current last card
      return next;
    });
  }, [batchSize, sortedProducts.length]);

  // Handle dropdown search (ONLY canonical names now)
  const handleDropdownSearch = (canonicalNames) => {
    if (process.env.NODE_ENV === 'development') { 
      console.log('🔍 handleDropdownSearch called with:', canonicalNames);
    }
    const canonicalNamesArray = Array.isArray(canonicalNames) ? canonicalNames : [];
    
    // Find which canonical names are new compared to current selection
    const newCanonicalNames = canonicalNamesArray.filter(name => 
      !selectedCanonicalNames.includes(name)
    );
    
    // Track each new flavor selection as a separate search event
    newCanonicalNames.forEach(name => {
      trackProductSearch({
        searchTerm: name,
        canonicalNames: [name]
      });
    });
    
    // Update state
    setSelectedCanonicalNames(canonicalNamesArray);
    setDisplayCount(20);
    setShowGrid(false); // Always hide grid when doing a search
    searchCache.current = {};
    apiCallsRef.current.clear();
    
    // Update URL immediately
    updateUrlWithSearch(canonicalNamesArray, sortBy);
    
    // Trigger search
    if (process.env.NODE_ENV === 'development') { 
      console.log('🚀 Triggering fetchFilteredProducts with canonical names:', canonicalNamesArray);
    }
    fetchFilteredProducts(canonicalNamesArray);
  };

  // Handle sort change
  const handleSortChange = (e) => {
    const newSortValue = e.target.value;
    setSortBy(newSortValue);
    setDisplayCount(20);
    searchCache.current = {};
    apiCallsRef.current.clear();
    
    // Update URL with new sort parameter
    updateUrlWithSearch(selectedCanonicalNames, newSortValue);
    
    // Trigger new search
    fetchFilteredProducts(selectedCanonicalNames);
  };

  // Robust function to scroll sibling card into view using refs
  const scrollSiblingCardIntoView = useCallback((closedIndex, total) => {
    // Try left first
    let targetIndex = closedIndex > 0 ? closedIndex - 1 : null;
    // If no left sibling, try right sibling
    if (targetIndex === null && closedIndex < total - 1) {
      targetIndex = closedIndex + 1;
    }
    // Scroll the target card into view if it exists
    if (targetIndex !== null && cardRefs.current[targetIndex]) {
      cardRefs.current[targetIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    }
  }, []);

  // Render
  if (errorState.hasError) {
    return (
      <ProductsUnavailable 
        onRetry={() => {
          setErrorState({ hasError: false, message: '' });
          fetchFilteredProducts();
        }}
        retryCountdown={120}
      />
    );
  }

  if (showGrid && canonicalProducts.length > 0) {
    return (
      <div className="store-location product-search-page">
        <div className="product-grid-container">
          <ProductGrid
            products={canonicalProducts}
            onSelect={product => {
              // Track the grid selection in analytics (consolidated into product_search event)
              // Convert to canonical names and trigger search
              handleDropdownSearch([product.canonicalName || product.displayName]);
              setShowGrid(false);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="store-location product-search-page">
      {/* Removed global loader - we now show loaders per card */}
      
      {/* Filter Form with Back Button and Sort Options - DROPDOWN ONLY */}
      <div className="filter-container" style={{ maxWidth: '900px', margin: '0 auto', marginBottom: '32px', padding: '24px 32px', background: '#fff', borderRadius: '18px', boxShadow: '0 2px 12px rgba(13,110,253,0.07)' }}>
        {/* Back to Product View button above all filters, inside filter container, centered */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
          <button
            type="button"
            className="back-to-product-view-btn"
            style={{
              background: '#0d6efd',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '10px 32px',
              fontSize: '1rem',
              fontWeight: '700',
              boxShadow: '0 2px 8px rgba(13,110,253,0.10)',
              cursor: 'pointer',
              transition: 'background 0.2s, box-shadow 0.2s, transform 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              outline: 'none',
            }}
            onClick={() => {
              // Update the state to show the product grid instead of navigating
              // console.log('Setting showGrid to true');
              setShowGrid(true);
              
              // Clear any selected products to reset the search state
              setSelectedCanonicalNames([]);
              
              // Get current URL and preserve path and necessary parameters
              const currentUrl = new URL(window.location.href);
              
              // Check if we're in embedded mode (important for the back button to work)
              const isEmbedded = window.self !== window.top || 
                                window.STORE_LOCATOR_CONFIG?.isEmbedded === true ||
                                window.location.hostname === 'getgoodfeels.com';
              
              // Keep the current pathname (e.g., /buy-now)
              const newUrl = new URL(currentUrl.pathname, window.location.origin);
              
              // Set tab to product-search
              newUrl.searchParams.set('tab', 'product-search');
              
              // Remove the canonicalNames parameter
              newUrl.searchParams.delete('canonicalNames');
              
              // Preserve location parameter if it exists
              if (currentUrl.searchParams.has('location')) {
                newUrl.searchParams.set('location', currentUrl.searchParams.get('location'));
              }
              
              // Preserve lat/lng parameters if they exist
              if (currentUrl.searchParams.has('lat')) {
                newUrl.searchParams.set('lat', currentUrl.searchParams.get('lat'));
              }
              if (currentUrl.searchParams.has('lng')) {
                newUrl.searchParams.set('lng', currentUrl.searchParams.get('lng'));
              }
              
              // Log the state change for debugging
              if (process.env.NODE_ENV === 'development') {
                console.log('Switching to product grid view, updated URL:', newUrl.toString());
              }
              
              // Update URL without causing a page reload
              window.history.pushState({}, '', newUrl.toString());
              
              // If we're in embedded mode, notify the parent about the view change
              if (isEmbedded && window.parent && typeof window.parent.postMessage === 'function') {
                try {
                  window.parent.postMessage({
                    type: 'viewChange',
                    view: 'product-grid',
                    url: newUrl.toString()
                  }, '*');
                  console.log('Sent postMessage viewChange notification to parent');
                } catch (e) {
                  console.error('Error with postMessage notification:', e);
                }
              }
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#0b5ed7'; e.currentTarget.style.transform = 'scale(1.04)'; }}
            onMouseOut={e => { e.currentTarget.style.background = '#0d6efd'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{marginRight: '6px'}} xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 5L7.5 10L12.5 15" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Product View
          </button>
        </div>
        <div className="filter-form">
          <div className="filter-row" style={{ display: 'flex', gap: '32px' }}>
            <div className="filter-field product-name-field" style={{ minWidth: '260px', flex: '1 1 50px' }}>
              <label htmlFor="product-dropdown">Select Products</label>
              <div className="dropdown-with-button">
                <ProductDropdown
                  value=""
                  onChange={() => {}} // Not needed anymore
                  onSearch={handleDropdownSearch}
                  isLoading={isFiltering}
                  selectedCanonicalNames={selectedCanonicalNames}
                />
              </div>
            </div>
            <div className="filter-field sort-field" style={{ minWidth: '200px', flex: '1 1 50px' }}>
              <label htmlFor="sort-by">Sort By</label>
              <SortDropdown
                value={sortBy}
                onChange={handleSortChange}
                shopperLocation={shopperLocation}
              />
            </div>
            <div className="filter-field filters-field" style={{ minWidth: '220px', flex: '1 1 50px' }}>
              <label htmlFor="product-filters">Filters</label>
              <ProductFilterDropdown
                filters={productFilters}
                onChange={setProductFilters}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Results Section */}
      {isLoading ? (
        <div className="loading-message">
          <p>Loading products...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="no-results-message">
          <h3>No products found</h3>
          <p>Try adjusting your search criteria</p>
        </div>
      ) : (
        <>
          <div className="results-count">
            Showing {visibleProducts.length} of {sortedProducts.length} products
            {hasMore && <span className="load-more-hint"> - Scroll for more</span>}
          </div>
          <div className="product-list" id="product-grid">
            {visibleProducts.map((product, index) => {
              const config = product.configs?.[0];
              const onSale = config && config.salePrice && config.salePrice < config.normalPrice;
              const store = storeInfo[product.retailerId] || {
                name: 'Unknown Store',
                city: '',
                state: '',
                distance: null
              };
              if (product.retailerAddress) store.address = product.retailerAddress;
              if (product.retailerPhone) store.phone = product.retailerPhone;
              if (product.retailerHours) store.hours = product.retailerHours;
              if (product.menuTypes?.includes('recreational') && product.recreationalURL) {
                store.websiteUrl = product.recreationalURL;
              } else if (product.menuTypes?.includes('medical') && product.medicalURL) {
                store.websiteUrl = product.medicalURL;
              } else if (product.retailerWebsite) {
                store.websiteUrl = product.retailerWebsite;
              }
              const price = config?.salePrice || config?.normalPrice || 0;
              const distance = store.distance || null;
              const painMetric = calculatePainMetric(price, distance);
              const cacheKey = `${product.name}-${price}`;
              const canonical = canonicalProductCache.get(cacheKey) || getCanonicalProductSync(product.name) || null;
              const displayName = canonical ? canonical.canonicalName : product.name;
              let hasValidUrl = false;
              if (product.menuTypes?.includes('recreational')) {
                hasValidUrl = !!product.recreationalURL && product.recreationalURL.startsWith('http');
              } else if (product.menuTypes?.includes('medical')) {
                hasValidUrl = !!product.medicalURL && product.medicalURL.startsWith('http');
              }
              return (
                <ProductItem
                  key={product.id}
                  product={product}
                  store={store}
                  config={config}
                  onSale={onSale}
                  sortBy={sortBy}
                  painMetric={painMetric}
                  displayName={displayName}
                  hasValidUrl={hasValidUrl}
                  canonicalProduct={canonical}
                  expandedCardId={expandedCardId}
                  setExpandedCardId={setExpandedCardId}
                  allProducts={allProducts}
                  globalLoaderState={globalLoaderState}
                  setGlobalLoaderState={setGlobalLoaderState}
                  onScrollSiblingIntoView={scrollSiblingCardIntoView}
                  productIndex={index}
                  visibleProductsLength={visibleProducts.length}
                  ref={el => cardRefs.current[index] = el}
                />
              );
            })}
          </div>
          {hasMore && !pauseAutoLoad && (
            <div className="loading-more">Scroll for more products...</div>
          )}
          {hasMore && pauseAutoLoad && (
            <div className="load-more-container">
              <button type="button" className="load-more-btn" onClick={handleLoadMoreClick}
                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(13,110,253,0.18)'; }}
                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(13,110,253,0.14)'; }}
              >
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

ProductSearch.propTypes = {
  shopperLocation: PropTypes.object,
  initialSearchTerm: PropTypes.string
};

export default ProductSearch;
